import { useCallback, useEffect } from 'react';

import { useCartWithServerOrderSurcharges } from 'features/cart/hooks/use-cart-with-server-order-surcharges';
import {
  AmplitudeCheckoutSection,
  getCheckoutChangeButtonEventAttributes,
} from 'features/checkout/utils/analytics';
import { useCheckoutPaymentDiagnosticContributor } from 'features/diagnostics/contributors/checkout-payment-contributor';

import { IBackendCart } from '@rbi-ctg/menu';
import { CartPaymentCardType } from 'generated/graphql-gateway';
import { CustomEventNames, EventTypes, logRBIEvent } from 'state/amplitude';

import { usePrepaidsPaymentMethods } from '../hooks/prepaid';
import { usePaymentContext } from '../state';
import { CardTypes, IPaymentMethod } from '../types';

import { PaymentMethodSelector } from './payment-method-selector';
import { SelectionType } from './payment-method-selector/types';

const MODAL_OPEN_DELAY = 300;

export type CheckoutPaymentSelectorProps = {
  cart: IBackendCart | null | undefined;
  checkoutPaymentMethod: IPaymentMethod | undefined;
  checkoutPaymentMethodId: string | null | undefined;
  isCheckoutLoading: boolean;
  isLoadingPaymentMethods: boolean;
  isSubmittingOrder: boolean;
  paymentMethods: IPaymentMethod[];
  reloadAmount: number;
  showReloadPaymentMethods: boolean;
};

export function CheckoutPaymentSelector({
  cart,
  checkoutPaymentMethod,
  checkoutPaymentMethodId,
  isCheckoutLoading,
  isLoadingPaymentMethods,
  isSubmittingOrder,
  paymentMethods,
  reloadAmount,
  showReloadPaymentMethods,
}: CheckoutPaymentSelectorProps) {
  const {
    canUseApplePay,
    canUseGooglePay,
    getBalanceFromPaymentMethods,
    hasGetPaymentMethodsError,
    noPaymentMethodsAvailable,
    setAccountToDelete: setAccountToUpdate,
    setCheckoutPaymentMethodId,
    setDefaultPaymentMethodId,
    setDefaultReloadPaymentMethodId,
    setIsAddCreditCardModalVisible,
    setIsAddReloadPrepaidMethodModalVisible,
    setIsAddUpdateCardForReloadModalVisible,
    setPrepaidReloadPaymentMethodId,
  } = usePaymentContext();
  const { reloadPaymentMethods, selectedReloadPaymentMethod } = usePrepaidsPaymentMethods();
  const { cartTotalCents: orderTotal } = useCartWithServerOrderSurcharges(cart);
  const isSelectorLoading =
    (!isSubmittingOrder && isLoadingPaymentMethods) ||
    (!noPaymentMethodsAvailable && paymentMethods.length === 0);

  useCheckoutPaymentDiagnosticContributor({
    paymentMethods,
    selectedPaymentMethod: checkoutPaymentMethod,
    loading: isSelectorLoading,
    hasError: hasGetPaymentMethodsError,
    noPaymentMethodsAvailable,
    selectorVisible: !isSelectorLoading,
    canUseApplePay,
    canUseGooglePay,
  });

  const handleSelectPaymentMethod = useCallback(
    (type: SelectionType, id?: string) => {
      switch (type) {
        case 'add':
          setAccountToUpdate('');
          setTimeout(() => {
            if (id?.includes('reload-')) {
              setIsAddUpdateCardForReloadModalVisible(true);
            } else {
              setIsAddCreditCardModalVisible(true);
            }
          }, MODAL_OPEN_DELAY);
          break;

        case 'update': {
          const isReloadUpdate = id?.startsWith('reload-') ?? false;
          setAccountToUpdate(isReloadUpdate ? (id ?? '').replace('reload-', '') : id ?? '');
          setTimeout(() => {
            if (isReloadUpdate) {
              setIsAddUpdateCardForReloadModalVisible(true);
            } else {
              setIsAddCreditCardModalVisible(true);
            }
          }, MODAL_OPEN_DELAY);
          break;
        }

        case 'prepaid':
          setTimeout(() => {
            setIsAddReloadPrepaidMethodModalVisible(true);
          }, MODAL_OPEN_DELAY);
          break;

        case 'selection':
          if (showReloadPaymentMethods && id?.includes('prepaid-')) {
            const sanitizedId = id.replace('prepaid-', '');
            setPrepaidReloadPaymentMethodId(sanitizedId);
            setDefaultReloadPaymentMethodId(sanitizedId);
          } else {
            setDefaultPaymentMethodId(id ?? '');
            setCheckoutPaymentMethodId(id ?? '');
          }
          break;
      }
    },
    [
      setAccountToUpdate,
      setCheckoutPaymentMethodId,
      setDefaultPaymentMethodId,
      setDefaultReloadPaymentMethodId,
      setIsAddCreditCardModalVisible,
      setIsAddReloadPrepaidMethodModalVisible,
      setIsAddUpdateCardForReloadModalVisible,
      setPrepaidReloadPaymentMethodId,
      showReloadPaymentMethods,
    ]
  );

  useEffect(() => {
    if (!checkoutPaymentMethod) {
      return;
    }

    const cardType = checkoutPaymentMethod.credit?.cardType;
    const isCreditCard =
      !!cardType && cardType !== CardTypes.APPLE_PAY && cardType !== CardTypes.GOOGLE_PAY;
    let paymentInfo: { paymentMethod: string; cardBrand?: string };

    if (isCreditCard) {
      paymentInfo = {
        paymentMethod: CartPaymentCardType.CREDIT,
        cardBrand: cardType,
      };
    } else if (checkoutPaymentMethod.prepaid) {
      paymentInfo = {
        paymentMethod: CartPaymentCardType.GIFT_CARD,
      };
    } else {
      paymentInfo = {
        paymentMethod: checkoutPaymentMethod.accountIdentifier ?? '',
      };
    }

    logRBIEvent(
      {
        name: CustomEventNames.PAYMENT_METHOD_SELECTED,
        type: EventTypes.Other,
        attributes: paymentInfo,
      },
      { skipLoggingToBraze: true }
    );
  }, [checkoutPaymentMethod]);

  return (
    <PaymentMethodSelector
      isLoading={!isSubmittingOrder && isLoadingPaymentMethods}
      methods={paymentMethods}
      selectedMethodId={checkoutPaymentMethodId ?? undefined}
      noPaymentMethodsAvailable={noPaymentMethodsAvailable}
      isDisabled={isCheckoutLoading}
      changeButtonEventAttributes={getCheckoutChangeButtonEventAttributes(
        AmplitudeCheckoutSection.PaymentMethod
      )}
      prepaid={{
        shouldReloadPrepaidMethod: showReloadPaymentMethods,
        reloadAmount,
        allowedMethodsForReload: reloadPaymentMethods,
        selectedMethodId:
          (selectedReloadPaymentMethod?.accountIdentifier ||
            selectedReloadPaymentMethod?.fdAccountId) ??
          undefined,
        prepaidBalance: checkoutPaymentMethod
          ? getBalanceFromPaymentMethods(checkoutPaymentMethod)
          : 0,
        orderTotal,
      }}
      onSelect={handleSelectPaymentMethod}
    />
  );
}
